'use client';

import React, { memo } from 'react';
import { alpha, Box, Button } from '@mui/material';

export interface TabOption<T extends string = string> {
  value: T;
  label: string;
}

interface TabSwitcherProps<T extends string = string> {
  activeTab: T;
  onTabChange: (tab: T) => void;
  options: TabOption<T>[];
  containerSx?: any;
  variant?: 'pills' | 'subtle-pills' | 'text';
}

const TabItem = memo(
  ({
    option,
    isActive,
    variant,
    onTabChange,
  }: {
    option: TabOption<any>;
    isActive: boolean;
    variant: 'pills' | 'subtle-pills' | 'text';
    onTabChange: (value: any) => void;
  }) => {
    const handleClick = () => onTabChange(option.value);

    return (
      <Button
        size="small"
        variant="text"
        onClick={handleClick}
        sx={{
          fontSize: variant === 'subtle-pills' ? '0.875rem' : '0.95rem',
          borderRadius: variant === 'text' ? '0' : variant === 'subtle-pills' ? 1.5 : '8px',
          textTransform: 'none',
          fontWeight: isActive ? 700 : 500,
          minWidth: 'auto',
          px: variant === 'text' ? 0 : 2,
          py: variant === 'subtle-pills' ? 0.5 : 1,
          bgcolor: isActive
            ? variant === 'subtle-pills'
              ? (theme) => alpha(theme.palette.text.primary, 0.08)
              : variant === 'pills'
                ? 'primary.main'
                : 'transparent'
            : 'transparent',
          color: isActive
            ? variant === 'pills'
              ? 'primary.contrastText'
              : 'text.primary'
            : 'text.secondary',
          border: 'none',
          borderBottom: 'none',
          mb: 0,
          whiteSpace: 'nowrap',
          '&:hover': {
            bgcolor:
              variant === 'subtle-pills'
                ? (theme) => alpha(theme.palette.text.primary, 0.1)
                : variant === 'pills'
                  ? isActive
                    ? 'primary.dark'
                    : (theme: any) => alpha(theme.palette.primary.main, 0.05)
                  : 'transparent',
            color: 'text.primary',
          },
        }}
      >
        {option.label}
      </Button>
    );
  }
);

export function TabSwitcher<T extends string>({
  activeTab,
  onTabChange,
  options,
  containerSx = {},
  variant = 'pills',
}: TabSwitcherProps<T>) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: variant === 'text' ? 3 : 1,
        mb: 2,
        px: variant === 'text' || variant === 'subtle-pills' ? 0 : 1,
        bgcolor:
          variant === 'subtle-pills'
            ? (theme) => alpha(theme.palette.text.primary, 0.05)
            : 'transparent',
        borderRadius: variant === 'subtle-pills' ? 2 : 0,
        borderBottom: 'none',
        overflowX: 'auto',
        overflowY: 'hidden',
        pb: variant === 'text' ? '2px' : 0,
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        ...containerSx,
      }}
    >
      {options.map((option) => (
        <TabItem
          key={option.value}
          option={option}
          isActive={(activeTab as string) === (option.value as string)}
          variant={variant}
          onTabChange={onTabChange}
        />
      ))}
    </Box>
  );
}
